import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { GOOGLE, GOOGLE_REVIEWS } from "../config/site";
import { GoogleMark, Stars } from "./GoogleMark";
import { Reveal } from "./Reveal";

// Google reviews — rotates verified Google review content with the live
// aggregate rating. Source data lives in src/config/site.js (GOOGLE_REVIEWS).
export function ReviewsSection() {
  const cards = [
    ...GOOGLE_REVIEWS.map((r) => ({ kind: "review", ...r })),
    { kind: "cta" },
  ];
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % cards.length), 5000);
    return () => clearInterval(t);
  }, [paused, cards.length]);

  const card = cards[index];

  return (
    <section
      data-testid="reviews-section"
      className="border-y border-brand-line bg-white py-16 lg:py-24"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="rounded-2xl border border-brand-line bg-brand-cream p-3">
                <GoogleMark className="h-7 w-7" />
              </span>
              <p className="text-xs font-display font-bold uppercase tracking-[0.3em] text-brand-golddeep">
                Google Reviews
              </p>
            </div>
            <h2 className="mt-6 text-4xl font-black text-brand-forest sm:text-5xl">
              DC Rates Us <span className="text-brand-golddeep">{GOOGLE.rating}</span> out of 5.
            </h2>
            <div className="mt-4 flex items-center gap-3">
              <Stars value={GOOGLE.rating} className="h-6 w-6" />
              <span className="text-sm font-medium text-brand-ink/55" data-testid="reviews-count">
                {GOOGLE.count} Google reviews
              </span>
            </div>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-brand-ink/60">
              {GOOGLE.placeName} — real reviews from real DC customers, straight from our Google
              Business listing.
            </p>
            <a
              href={GOOGLE.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-google-link"
              className="group mt-7 inline-flex items-center gap-2.5 rounded-full border-2 border-brand-pine px-7 py-3.5 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-pine transition-colors hover:bg-brand-pine hover:text-brand-cream"
            >
              Read All on Google
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-7">
            <div className="relative min-h-[19rem] sm:min-h-[16rem]" data-testid="reviews-carousel">
              <AnimatePresence mode="wait">
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -18 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="h-full rounded-[2rem] border border-brand-line bg-brand-cream p-8 sm:p-10"
                >
                  {card.kind === "review" ? (
                    <figure data-testid="review-card">
                      <Quote className="h-7 w-7 text-brand-gold" aria-hidden="true" />
                      <blockquote className="mt-5 font-display text-xl font-bold leading-snug text-brand-forest sm:text-2xl">
                        "{card.text}"
                      </blockquote>
                      <figcaption className="mt-6 flex flex-wrap items-center gap-3">
                        <Stars value={card.rating} />
                        <span className="text-sm font-bold text-brand-ink">{card.name}</span>
                        <span className="text-xs text-brand-ink/50">{card.date}</span>
                        <span className="flex items-center gap-1.5 rounded-full border border-brand-line bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-brand-ink/55">
                          <GoogleMark className="h-3 w-3" /> Verified Google review
                        </span>
                      </figcaption>
                    </figure>
                  ) : (
                    <div className="flex h-full flex-col items-start justify-center" data-testid="review-cta-card">
                      <div className="flex items-center gap-4">
                        <GoogleMark className="h-10 w-10" />
                        <span className="font-display text-5xl font-black text-brand-forest">{GOOGLE.rating}</span>
                        <Stars value={GOOGLE.rating} className="h-5 w-5" />
                      </div>
                      <p className="mt-4 max-w-md text-sm leading-relaxed text-brand-ink/60">
                        Join {GOOGLE.count} customers who've rated their CannaCheap DC experience on Google.
                      </p>
                      <a
                        href={GOOGLE.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="review-write-link"
                        className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-pine px-6 py-3 text-sm font-bold text-brand-cream transition-transform hover:scale-[1.03]"
                      >
                        Leave a Review <ArrowRight className="h-4 w-4" />
                      </a>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              <div className="mt-6 flex items-center justify-between">
                <div className="flex gap-2">
                  {cards.map((_, i) => (
                    <button
                      key={i}
                      data-testid={`review-dot-${i}`}
                      aria-label={`Show review ${i + 1}`}
                      onClick={() => setIndex(i)}
                      className={`h-2 rounded-full transition-all ${i === index ? "w-8 bg-brand-pine" : "w-2 bg-brand-line"}`}
                    />
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid="review-prev"
                    aria-label="Previous review"
                    onClick={() => setIndex((index - 1 + cards.length) % cards.length)}
                    className="rounded-full border border-brand-line bg-white p-2.5 text-brand-forest transition-colors hover:border-brand-gold"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    data-testid="review-next"
                    aria-label="Next review"
                    onClick={() => setIndex((index + 1) % cards.length)}
                    className="rounded-full border border-brand-line bg-white p-2.5 text-brand-forest transition-colors hover:border-brand-gold"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
